/**
 * PostHog analytics for SynergyOS maintainers — tracks MCP usage (sessions, tool calls).
 * Not user-facing. Key is injected at build time via SYNERGYOS_POSTHOG_KEY.
 * Override with POSTHOG_MCP_KEY for self-hosted deployments.
 */

import { userInfo } from "node:os";
import { PostHog } from "posthog-node";

let client: PostHog | null = null;
let distinctId = "anonymous";

const POSTHOG_HOST = "https://eu.i.posthog.com";

/** Injected at build time: SYNERGYOS_POSTHOG_KEY env when running `npm run build`/publish. */
declare const __SYNERGYOS_POSTHOG_KEY__: string;

/** Only write to stderr when MCP_DEBUG=1 for quieter default DX. */
function log(msg: string): void {
  if (process.env.MCP_DEBUG === "1") {
    process.stderr.write(msg);
  }
}

function getBuildTimeKey(): string {
  try {
    return __SYNERGYOS_POSTHOG_KEY__;
  } catch {
    // Not replaced by bundler (e.g. running via tsx in tests) — treat as absent.
    return "";
  }
}

export function initAnalytics(): void {
  const apiKey = process.env.POSTHOG_MCP_KEY || getBuildTimeKey();
  if (!apiKey) {
    log("[MCP-ANALYTICS] No PostHog key — tracking disabled (set SYNERGYOS_POSTHOG_KEY at build time for publish)\n");
    return;
  }

  client = new PostHog(apiKey, {
    host: POSTHOG_HOST,
    flushAt: 1,
    flushInterval: 5000,
    featureFlagsPollingInterval: 30_000,
  });
  distinctId = process.env.MCP_USER_ID || fallbackDistinctId();

  log(`[MCP-ANALYTICS] Initialized — host=${POSTHOG_HOST} distinctId=${distinctId}\n`);
}

function fallbackDistinctId(): string {
  try {
    return userInfo().username;
  } catch {
    return `os-${process.pid}`;
  }
}

export function trackSessionStarted(
  workspaceId: string,
  serverVersion: string,
): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "mcp_session_started",
    properties: {
      workspace_id: workspaceId,
      server_version: serverVersion,
      source: "mcp-server",
      $groups: { workspace: workspaceId },
    },
  });
}

export function trackToolCall(
  fn: string,
  status: "ok" | "error",
  durationMs: number,
  workspaceId: string,
  errorMsg?: string,
): void {
  const properties: Record<string, unknown> = {
    tool: fn,
    status,
    duration_ms: durationMs,
    workspace_id: workspaceId,
    source: "mcp-server",
    $groups: { workspace: workspaceId },
  };
  if (errorMsg) properties.error = errorMsg;

  if (!client) return;
  client.capture({
    distinctId,
    event: "mcp_tool_called",
    properties,
  });
}

/**
 * Per-tool+action call telemetry (WP-484 S1, Q3). Fired from
 * `runWithToolContext` in client.ts — the one chokepoint every compound tool
 * already wraps its handler body in. Feeds the next consolidation decision
 * (§2 of the design: no per-MCP-tool call-frequency signal existed before this).
 */
export function trackCompoundToolAction(
  tool: string,
  action: string | undefined,
  workspaceId: string,
): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "mcp_compound_tool_action",
    properties: {
      tool,
      action: action ?? null,
      workspace_id: workspaceId,
      source: "mcp-server",
      $groups: { workspace: workspaceId },
    },
  });
}

export function trackSetupStarted(): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "mcp_setup_started",
    properties: {
      source: "mcp-server",
      platform: process.platform,
    },
  });
}

export function trackSetupCompleted(
  chosenClient: string,
  outcome: "config_written" | "config_existed" | "snippet_shown" | "write_error",
): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "mcp_setup_completed",
    properties: {
      client: chosenClient,
      outcome,
      source: "mcp-server",
      platform: process.platform,
    },
  });
}

export function trackQualityVerdict(
  workspaceId: string,
  props: {
    entry_id: string;
    entry_type: string;
    tier: string;
    context: string;
    passed: boolean;
    source: string;
    criteria_total: number;
    criteria_failed: number;
    llm_scheduled: boolean;
    /** WP-475 E3: rubric methodology that produced the verdict (v1-vs-v2 seam). */
    methodology_version?: string;
  },
): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "quality_verdict_generated",
    properties: {
      ...props,
      workspace_id: workspaceId,
      source_system: "mcp-server",
      $groups: { workspace: workspaceId },
    },
  });
}

export function trackQualityCheck(
  workspaceId: string,
  props: {
    entry_id: string;
    entry_type: string;
    tier: string;
    passed: boolean;
    source: string;
    llm_status?: string;
    llm_duration_ms?: number;
    llm_error?: string;
    has_roger_martin: boolean;
  },
): void {
  if (!client) return;
  client.capture({
    distinctId,
    event: "quality_verdict_checked",
    properties: {
      ...props,
      workspace_id: workspaceId,
      source_system: "mcp-server",
      $groups: { workspace: workspaceId },
    },
  });
}

export type ClassifierReasonCategory =
  | "auto-routed"
  | "low-confidence"
  | "ambiguous"
  | "non-provisioned";

type CaptureClassifierTelemetryProps = {
  predicted_collection: string;
  confidence: number;
  auto_routed: boolean;
  reason_category: ClassifierReasonCategory;
  explicit_collection_provided: boolean;
};

function trackCaptureClassifierEvent(
  event: "mcp_capture_classifier_evaluated" | "mcp_capture_classifier_auto_routed" | "mcp_capture_classifier_fallback",
  workspaceId: string,
  props: CaptureClassifierTelemetryProps,
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event,
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics are advisory and must never break capture flow.
  }
}

export function trackCaptureClassifierEvaluated(
  workspaceId: string,
  props: CaptureClassifierTelemetryProps,
): void {
  trackCaptureClassifierEvent("mcp_capture_classifier_evaluated", workspaceId, props);
}

export function trackCaptureClassifierAutoRouted(
  workspaceId: string,
  props: CaptureClassifierTelemetryProps,
): void {
  trackCaptureClassifierEvent("mcp_capture_classifier_auto_routed", workspaceId, props);
}

export function trackCaptureClassifierFallback(
  workspaceId: string,
  props: CaptureClassifierTelemetryProps,
): void {
  trackCaptureClassifierEvent("mcp_capture_classifier_fallback", workspaceId, props);
}

/** GLO-26 / TEN-156: every SSOT commit for PostHog funnels (split auto vs manual). */
export function trackChainEntryCommitted(
  workspaceId: string,
  props: {
    entry_id: string;
    collection?: string;
    commit_method: "auto" | "manual";
    surface:
      | "mcp_commit_tool"
      | "mcp_capture"
      | "mcp_wrapup";
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "chain_entry_committed",
      properties: {
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
        ...props,
      },
    });
  } catch {
    // Analytics must never break the tool path.
  }
}

export function trackKnowledgeGap(
  workspaceId: string,
  props: {
    query: string;
    tool: string;
    action: string;
    gap_type: "search_zero" | "context_task_empty" | "context_entry_isolated" | "context_graph_empty";
    collection_scope?: string;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "knowledge_gap_detected",
      properties: {
        ...props,
        query: props.query.slice(0, 200),
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the tool response path.
  }
}

// ── BET-272 S6 / STD-155: Capture intelligence observability ────────────────

/** Fires when formative quality hints are returned at capture time. */
export function trackCaptureQualityHints(
  workspaceId: string,
  props: {
    collection: string;
    hint_count: number;
    hint_fields: string[];
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "mcp_capture_quality_hints",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break capture flow.
  }
}

/** Fires when relation suggestions are returned at capture time. */
export function trackCaptureRelationSuggestions(
  workspaceId: string,
  props: {
    collection: string;
    suggestion_count: number;
    relation_types: string[];
    avg_confidence: number;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "mcp_capture_relation_suggestions",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break capture flow.
  }
}

// ── BET-288 S0: Collection classification confusion matrix telemetry ────────

/**
 * Fires on every collection classification (auto-routed, fallback, explicit-provided).
 * Captures the full confusion signal: predicted collection, runner-up, thinkingLayer,
 * and confidence scores. This is the baseline measurement for BET-288 algorithm changes.
 */
export function trackCollectionClassified(
  workspaceId: string,
  props: {
    collection_slug: string;
    thinking_layer: string | null;
    confidence: number;
    classified_by: "llm" | "heuristic";
    confidence_tier: "high" | "medium" | "low";
    alternative_slug: string | null;
    alternative_confidence: number | null;
    explicit_collection_provided: boolean;
    auto_routed: boolean;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "collection_classified",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the capture flow.
  }
}

// ── BET-289 S5: Field-level writing guidance telemetry ────────────────────────

/** Fires when field guidance is injected into a capture prompt or response. */
export function trackFieldGuidanceApplied(
  workspaceId: string,
  props: {
    collection: string;
    guided_field_count: number;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "field_guidance_applied",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the capture flow.
  }
}

/** Fires when commit-time heuristic detects field guidance violations. */
export function trackFieldQualityWarning(
  workspaceId: string,
  props: {
    warning_count: number;
    warning_types: string[];
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "field_quality_warning",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the commit flow.
  }
}

/** Fires when skipGuidanceCheck is used to bypass guidance validation. */
export function trackFieldQualityOverride(
  workspaceId: string,
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "field_quality_override",
      properties: {
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the commit flow.
  }
}

// ── WP-306 S3: Write-Back Measurement (capture rate insights) ──────────────

/** Fires on session close to track session capture rate. */
export function trackSessionCaptureRate(
  workspaceId: string,
  props: {
    entries_created: number;
    entries_modified: number;
    relations_created: number;
    had_captures: boolean;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "session_capture_rate",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break session closure.
  }
}

/** Fires when captureAudit is triggered (activity but no captures). */
export function trackZeroCaptureAuditFired(
  workspaceId: string,
  props: {
    suggestion_count: number;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "zero_capture_audit_fired",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break wrapup flow.
  }
}

// ── WP-316 S1b: Capture contract observability ──────────────────────────────

/** Fires when a capture-contract resource request references an unknown collection slug. */
export function trackCaptureContractMiss(
  workspaceId: string,
  props: {
    slug: string;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "capture_contract_miss",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the resource response path.
  }
}

/** Fires when writeBackHints are included in an orient response. */
export function trackWriteBackHintServed(
  workspaceId: string,
  props: {
    hint_count: number;
    has_task: boolean;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "write_back_hint_served",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break orient flow.
  }
}

/**
 * WP-316 S1a: Fires when entries action=commit fails with a structured error code.
 * Enables observability on which validation errors block commits most often.
 */
export function trackCommitErrorByCode(
  workspaceId: string,
  props: {
    error_code: string;
    missing_field_count: number;
    field_error_count: number;
    entry_id: string;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "commit_error_by_code",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the commit error path.
  }
}

// ── WP-316 S2: Classifier divergence tracking ─────────────────────────────

/**
 * Fires when an agent provides an explicit collection that differs from the
 * classifier's top suggestion. Measures how often agents override the classifier
 * and whether those overrides are to known alternatives or completely off-roster.
 */
export function trackClassifierDivergence(
  workspaceId: string,
  props: {
    classifier_collection: string;
    agent_collection: string;
    classifier_confidence: number;
    classifier_tier: string;
    agent_in_candidates: boolean;
  },
): void {
  if (!client) return;
  try {
    client.capture({
      distinctId,
      event: "classifier_divergence",
      properties: {
        ...props,
        workspace_id: workspaceId,
        source_system: "mcp-server",
        $groups: { workspace: workspaceId },
      },
    });
  } catch {
    // Analytics must never break the capture flow.
  }
}

export function getPostHogClient(): PostHog | null {
  return client;
}

export async function shutdownAnalytics(): Promise<void> {
  await client?.shutdown();
}
